export interface IDroppedFile {
  error: string | null;
  file: {
    name: string;
    data: ArrayBuffer | null;
  };
}
export type FiletypeWhitelist = string[];

export type FileReaderFunc = (opts?: IFileReaderOptions) => (file: File) => Promise<IDroppedFile>;

export interface IFileReaderOptions {
  mimetypeWhitelist?: FiletypeWhitelist;
  sizeUpperLimit?: number; // bytes
}

export const ValidateMimetype = (settings: IFileReaderOptions, file: File) => {
  if (typeof settings.mimetypeWhitelist === "undefined") {
    return null;
  }

  if (settings.mimetypeWhitelist.length === 0) {
    return null;
  }

  if (settings.mimetypeWhitelist.indexOf(file.type) === -1) {
    return "file type not in whitelist";
  }

  return null;
};

export const ValidateFilesize = (settings: IFileReaderOptions, file: File) => {
  if (typeof settings.sizeUpperLimit === "undefined") {
    return null;
  }

  if (settings.sizeUpperLimit === 0) {
    return null;
  }

  if (file.size > settings.sizeUpperLimit) {
    return "file type not in whitelist";
  }

  return null;
};

export const generateFileReader: FileReaderFunc = (opts?: IFileReaderOptions) => {
  const settings: IFileReaderOptions = typeof opts === "undefined" ? {} : opts;

  return async (file: File) => {
    const result = [ValidateMimetype, ValidateFilesize].reduce<string | null>(
      (out, validateFunc) => {
        if (out !== null) {
          return out;
        }

        return validateFunc(settings, file);
      },
      null,
    );
    if (result !== null) {
      const droppedFile: IDroppedFile = {
        error: result,
        file: { name: file.name, data: null },
      };

      return droppedFile;
    }

    return readFile(file);
  };
};

export const readFile = async (file: File): Promise<IDroppedFile> => {
  return new Promise<IDroppedFile>(resolve => {
    const reader = new FileReader();
    reader.onabort = () =>
      resolve({ file: { name: file.name, data: null }, error: "file reading was rejected" });
    reader.onerror = () =>
      resolve({ file: { name: file.name, data: null }, error: "file reading has failed" });
    reader.onload = () =>
      resolve({
        error: null,
        file: { data: reader.result! as ArrayBuffer, name: file.name },
      });
    reader.readAsArrayBuffer(file);
  });
};

export const htmlEntities = (str: string): string =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export const escape = (s: string): string =>
  ("" + s) /* Forces the conversion to string. */
    .replace(/\\/g, "\\\\") /* This MUST be the 1st replacement. */
    .replace(/\t/g, "\\t") /* These 2 replacements protect whitespaces. */
    .replace(/\n/g, "\\n")
    .replace(/\u00A0/g, "\\u00A0") /* Useful but not absolutely necessary. */
    .replace(/&/g, "\\x26") /* These 5 replacements protect from HTML/XML. */
    .replace(/'/g, "\\x27")
    .replace(/"/g, "\\x22")
    .replace(/</g, "\\x3C")
    .replace(/>/g, "\\x3E");
